// ============================================================================
// MemoryCard — THE LEARNING MOMENT
// On arrival it plays a ~2.6s constellation animation (a new teal fact-node
// pulses into a dim knowledge web and draws edges to neighbors), then settles
// to a quiet one-row card. Pure CSS keyframes on inline SVG — no libs.
// Respects prefers-reduced-motion (200ms fade, no graph); any click skips.
// ============================================================================

import { useEffect, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { beoflowColors, typography, spacing } from '@/theme/muiTheme'
import type { RenderPayload, MemorySpec } from '@/types/chatRenders'
import { cardChromeSx, monoMetaSx } from './CardShell'

const SETTLE_MS = 2600

const DIM_NODE = 'rgba(248, 246, 244, 0.28)'
const HAIRLINE = 'rgba(248, 246, 244, 0.10)'
const LABEL_FILL = 'rgba(248, 246, 244, 0.30)'
const TEAL = beoflowColors.tealBase

// Fixed constellation layout (viewBox 0 0 520 120). The new fact is born at
// NEW_NODE and draws edges to the first three NEIGHBORS.
const NEW_NODE = { x: 260, y: 62 }
const NEIGHBORS = [
  { x: 196, y: 40 },
  { x: 322, y: 44 },
  { x: 292, y: 92 },
]
const DIM_NODES = [
  { x: 60, y: 30, r: 2.5 }, { x: 96, y: 78, r: 3 }, { x: 140, y: 52, r: 2.5 },
  { x: 170, y: 96, r: 2 }, { x: 238, y: 22, r: 2.5 }, { x: 366, y: 78, r: 2.5 },
  { x: 402, y: 34, r: 3 }, { x: 452, y: 66, r: 2.5 }, { x: 486, y: 96, r: 2 },
]
const WEB_LINES = [
  [60, 30, 140, 52], [96, 78, 140, 52], [140, 52, 196, 40], [170, 96, 196, 40],
  [238, 22, 196, 40], [238, 22, 322, 44], [322, 44, 402, 34], [366, 78, 322, 44],
  [402, 34, 452, 66], [452, 66, 486, 96], [292, 92, 366, 78],
] as const
const CLUSTER_LABELS = [
  { x: 84, y: 18, text: 'TRAINING' },
  { x: 420, y: 20, text: 'RECOVERY' },
  { x: 130, y: 112, text: 'HABITS' },
]

function Constellation({ category }: { category: string }) {
  return (
    <Box
      sx={{
        // Keyframes for the learning moment — pure CSS on inline SVG.
        '@keyframes memNodeIn': {
          from: { opacity: 0, transform: 'scale(0)' },
          to: { opacity: 1, transform: 'scale(1)' },
        },
        '@keyframes memPulse': {
          '0%': { transform: 'scale(0.4)', opacity: 0.9 },
          '100%': { transform: 'scale(2.6)', opacity: 0 },
        },
        '@keyframes memEdge': {
          from: { strokeDashoffset: 1 },
          to: { strokeDashoffset: 0 },
        },
        '@keyframes memWarm': {
          from: { fill: DIM_NODE },
          to: { fill: TEAL },
        },
        '& .mem-new': {
          transformBox: 'fill-box',
          transformOrigin: 'center',
          animation: 'memNodeIn 320ms ease-out both',
          animationDelay: '150ms',
        },
        '& .mem-pulse': {
          transformBox: 'fill-box',
          transformOrigin: 'center',
          // Double pulse ring.
          animation: 'memPulse 850ms ease-out 2 both',
          animationDelay: '420ms',
        },
        '& .mem-edge': {
          strokeDasharray: 1,
          strokeDashoffset: 1,
          animation: 'memEdge 650ms ease-out both',
        },
        '& .mem-edge-1': { animationDelay: '650ms' },
        '& .mem-edge-2': { animationDelay: '850ms' },
        '& .mem-edge-3': { animationDelay: '1050ms' },
        '& .mem-warm': { animation: 'memWarm 600ms ease-out both' },
        '& .mem-warm-1': { animationDelay: '1150ms' },
        '& .mem-warm-2': { animationDelay: '1350ms' },
        '& .mem-warm-3': { animationDelay: '1550ms' },
        '@media (prefers-reduced-motion: reduce)': {
          '& .mem-new, & .mem-pulse, & .mem-edge, & .mem-warm': { animation: 'none' },
          '& .mem-edge': { strokeDashoffset: 0 },
        },
      }}
    >
      <svg viewBox="0 0 520 120" width="100%" height="120" aria-hidden="true">
        {/* Hairline web */}
        {WEB_LINES.map(([x1, y1, x2, y2], i) => (
          <line key={`w-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={HAIRLINE} strokeWidth={1} />
        ))}
        {/* Dim nodes */}
        {DIM_NODES.map((n, i) => (
          <circle key={`n-${i}`} cx={n.x} cy={n.y} r={n.r} fill={DIM_NODE} />
        ))}
        {/* Cluster labels — mono, tiny, dim */}
        {CLUSTER_LABELS.map(l => (
          <text
            key={l.text}
            x={l.x}
            y={l.y}
            fill={LABEL_FILL}
            style={{ fontFamily: typography.fontFamilyMonospace, fontSize: 7, letterSpacing: 1.2 }}
          >
            {l.text}
          </text>
        ))}
        {/* Edges from the new fact to its neighbors (drawn in) */}
        {NEIGHBORS.map((n, i) => (
          <line
            key={`e-${i}`}
            className={`mem-edge mem-edge-${i + 1}`}
            x1={NEW_NODE.x}
            y1={NEW_NODE.y}
            x2={n.x}
            y2={n.y}
            pathLength={1}
            stroke={`${TEAL}99`}
            strokeWidth={1}
          />
        ))}
        {/* Neighbor nodes warming to teal */}
        {NEIGHBORS.map((n, i) => (
          <circle key={`nb-${i}`} className={`mem-warm mem-warm-${i + 1}`} cx={n.x} cy={n.y} r={3} fill={DIM_NODE} />
        ))}
        {/* The new fact: teal node + double pulse ring */}
        <circle className="mem-pulse" cx={NEW_NODE.x} cy={NEW_NODE.y} r={7} fill="none" stroke={TEAL} strokeWidth={1} />
        <circle className="mem-new" cx={NEW_NODE.x} cy={NEW_NODE.y} r={4} fill={TEAL} />
        {/* The new fact's category label */}
        <text
          className="mem-new"
          x={NEW_NODE.x + 10}
          y={NEW_NODE.y - 8}
          fill={TEAL}
          style={{ fontFamily: typography.fontFamilyMonospace, fontSize: 7, letterSpacing: 1.2 }}
        >
          {category.toUpperCase()}
        </text>
      </svg>
    </Box>
  )
}

interface MemoryCardProps {
  render: RenderPayload
  /** True when the render arrived live in this session (plays the moment). */
  animate?: boolean
}

export default function MemoryCard({ render, animate = false }: MemoryCardProps) {
  const spec = render.spec as unknown as Partial<MemorySpec>
  const reducedMotion = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true,
    []
  )
  // Captured at mount: hydrated messages settle immediately, live arrivals play.
  const [phase, setPhase] = useState<'constellation' | 'settled'>(
    animate && !reducedMotion ? 'constellation' : 'settled'
  )
  const fadeIn = animate && reducedMotion

  useEffect(() => {
    if (phase !== 'constellation') return
    const t = window.setTimeout(() => setPhase('settled'), SETTLE_MS)
    return () => window.clearTimeout(t)
  }, [phase])

  const category = spec.category ?? render.entity.kind ?? 'memory'
  const isDelete = spec.action === 'delete' || render.status === 'removed'
  const showOldValue = Boolean(spec.old_value) && render.status === 'updated'

  // TODO: wire Edit/Forget to the memory tools once the backend exposes them.
  const handleEdit = () => {}
  const handleForget = () => {}

  return (
    <Box
      onClick={phase === 'constellation' ? () => setPhase('settled') : undefined}
      sx={{
        ...cardChromeSx,
        my: spacing.xs,
        px: spacing.md,
        py: spacing.sm,
        cursor: phase === 'constellation' ? 'pointer' : 'default',
        ...(fadeIn
          ? {
              '@keyframes memFade': { from: { opacity: 0 }, to: { opacity: 1 } },
              animation: 'memFade 200ms ease-out both',
            }
          : {}),
      }}
    >
      {phase === 'constellation' ? (
        <Box>
          {/* Header row while learning: pulsing teal glyph + mono label (frames). */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              pb: '7px',
              mb: spacing.xs,
              borderBottom: '1px solid rgba(248, 246, 244, 0.09)',
              '@keyframes memGlyphPulse': {
                '0%, 100%': { opacity: 0.3 },
                '50%': { opacity: 1 },
              },
            }}
          >
            <Box
              component="span"
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: TEAL,
                flexShrink: 0,
                animation: 'memGlyphPulse 1.4s ease-in-out infinite',
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}
            />
            <Typography
              sx={{
                fontFamily: typography.fontFamilyMonospace,
                fontSize: '9px',
                textTransform: 'uppercase',
                letterSpacing: '1.2px',
                color: 'rgba(248, 246, 244, 0.38)',
              }}
            >
              Memory · Flow is learning
            </Typography>
          </Box>
          <Constellation category={category} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm,
            minWidth: 0,
            ...(animate && !reducedMotion
              ? {
                  '@keyframes memSettle': {
                    from: { opacity: 0, transform: 'translateY(4px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  animation: 'memSettle 250ms ease-out both',
                }
              : {}),
          }}
        >
          {/* Teal ring with + (or − when forgetting) */}
          <Box
            component="span"
            sx={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              border: `1.5px solid ${TEAL}`,
              color: TEAL,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: typography.fontFamilyMonospace,
              fontSize: '11px',
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {isDelete ? '−' : render.status === 'updated' ? '✎' : '+'}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '13px', ...(isDelete ? { color: 'text.secondary' } : {}) }}>
              {showOldValue && (
                <Box component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary', mr: spacing.xs }}>
                  {spec.old_value}
                </Box>
              )}
              {spec.content ?? render.entity.title}
            </Typography>
            <Typography noWrap sx={monoMetaSx}>
              Memory · {category}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: spacing.md, flexShrink: 0 }}>
            <Typography
              component="span"
              onClick={handleEdit}
              sx={{ ...monoMetaSx, cursor: 'pointer', '&:hover': { color: 'text.primary' } }}
            >
              Edit
            </Typography>
            <Typography
              component="span"
              onClick={handleForget}
              sx={{ ...monoMetaSx, cursor: 'pointer', '&:hover': { color: 'text.primary' } }}
            >
              Forget
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  )
}
